import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { faEdit } from '@fortawesome/pro-duotone-svg-icons';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Button, Chip, Container, useTheme } from '@mui/material';

import { riskQueries } from '@repo/api/risk/risks';
import { useUser } from '@repo/auth';
import { DataDisplay } from '@repo/features/dataDisplay';
import { StatusBox } from '@repo/ui/StatusBox';

import { Route as EditRoute } from '~pages/_risks/risk.$riskId/(nopr)/edit.$page';

export const Route = createFileRoute('/_risks/risk/$riskId/_risk/')({
	beforeLoad: ({ context }) => {
		if (!context.user?.permissions.includes('risk.view')) {
			throw redirect({
				to: '/',
			});
		}
	},
	loader: ({ context, params }) => {
		void context.queryClient.ensureQueryData(riskQueries.id(params.riskId));
	},
	component: function RiskDetailsTab() {
		const { riskId } = Route.useParams();

		const { data: risk } = useSuspenseQuery(riskQueries.id(riskId));

		const navigate = Route.useNavigate();

		const theme = useTheme();

		const { permissions } = useUser();

		return (
			<Container maxWidth={false} sx={{ marginLeft: 0 }}>
				<Box
					sx={{
						display: 'flex',
						gap: 2,
						alignItems: 'flex-start',
					}}
				>
					<Box sx={{ flexGrow: 1 }}>
						<StatusBox
							label="Risk Details"
							color={
								risk.noprCompleted ?
									theme.palette.success
								:	theme.palette.yellow
							}
							bgcolor={
								risk.noprCompleted ?
									theme.palette.green.light
								:	theme.palette.yellow.light
							}
						>
							{risk.noprCompleted ?
								<Chip
									label="Completed"
									size="small"
									sx={({ palette }) => ({
										borderWidth: 1,
										borderStyle: 'solid',
										color: palette.success.dark,
										backgroundColor: 'transparent',
										'& .MuiChip-icon': {
											height: '0.7em',
											marginLeft: '8px',
										},
									})}
									icon={<FontAwesomeIcon icon={faCheck} color="currentColor" />}
								/>
							:	<Chip
									label="Draft"
									size="small"
									sx={{
										color: 'offwhite.contrastText',
										borderWidth: 1,
										borderStyle: 'solid',
										backgroundColor: 'offwhite.main',
									}}
								/>
							}
							{permissions.includes('risk.edit') && (
								<Button
									disabled={risk.stage === 'Closed'}
									variant="outlined"
									size="small"
									color="button"
									startIcon={<FontAwesomeIcon icon={faEdit} />}
									sx={{
										'& .MuiButton-startIcon svg': {
											fontSize: '1rem',
										},
									}}
									onClick={() => {
										void navigate({
											to: EditRoute.to,
											params: (params) => ({
												...params,
												page: 'insured',
											}),
										});
									}}
								>
									Edit
								</Button>
							)}
						</StatusBox>
					</Box>
				</Box>
				<DataDisplay data={risk.riskDetails} />
			</Container>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Risk', 'Details'),
});
